import { type MaybeRef, computed, unref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import {
  EMPTY_STATEMENTS_STATUSES_RESULT,
  type OpportunityStatementsStatusesResult,
  getOpportunityStatementsStatuses,
} from '~/api/opportunities/bankAccounts/statements/statuses';
import type { BankAccountStatementStatus } from '~/interfaces/opportunities/bankAccounts/statements/statuses/main';
import { DocumentState } from '~/interfaces/opportunities/documents/main';

export const opportunityStatementsStatusesKeys = {
  root: (hashId?: string | null) => ['opportunities', hashId ?? '', 'bank-account-statements', 'status'] as const,
  list: (hashId?: string | null, params?: string | null) =>
    ['opportunities', hashId ?? '', 'bank-account-statements', 'status', params ?? ''] as const,
};

type EnabledRef = MaybeRef<boolean | undefined>;

interface UseOpportunityStatementsStatusesOptions {
  enabled?: EnabledRef;
}

export function useOpportunityStatementsStatusesQuery (
  opportunityHashId: MaybeRef<string | null | undefined>,
  params: MaybeRef<string | null | undefined> = null,
  options: UseOpportunityStatementsStatusesOptions = {},
) {
  const queryClient = useQueryClient();

  const enabled = computed<boolean>(() => {
    const requested = options.enabled === undefined ? true : Boolean(unref(options.enabled));
    const resolvedId = unref(opportunityHashId);
    return requested && Boolean(resolvedId);
  });

  const queryKey = computed(() => opportunityStatementsStatusesKeys.list(unref(opportunityHashId) ?? null, unref(params) ?? null));

  const query = useQuery<OpportunityStatementsStatusesResult>({
    queryKey,
    queryFn: () => {
      const resolvedId = unref(opportunityHashId);
      if (!resolvedId) { return EMPTY_STATEMENTS_STATUSES_RESULT; }
      return getOpportunityStatementsStatuses(resolvedId, unref(params) ?? null);
    },
    enabled,
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
    initialData: () => queryClient.getQueryData<OpportunityStatementsStatusesResult>(queryKey.value) ?? EMPTY_STATEMENTS_STATUSES_RESULT,
  });

  const rawStatuses = computed<BankAccountStatementStatus[]>(() => query?.data?.value?.items ?? []);
  const alert = computed<string | null>(() => query?.data?.value?.alert ?? null);

  // Filtre central : un statement appartenant à un doc en cours d'extraction est masqué.
  const statuses = computed<BankAccountStatementStatus[]>(
    () => rawStatuses.value.filter(s => s.document?.state !== DocumentState.ExtractionStarted),
  );

  const setData = (items: BankAccountStatementStatus[], alertOverride?: string | null): void => {
    const currentRaw = query?.data?.value?.raw ?? EMPTY_STATEMENTS_STATUSES_RESULT.raw;
    queryClient.setQueryData(queryKey.value, {
      items: [...items],
      alert: alertOverride ?? query?.data?.value?.alert ?? null,
      raw: {
        ...currentRaw,
        data: [...items],
      },
    } satisfies OpportunityStatementsStatusesResult);
  };

  const markDocumentAsExtracting = (documentId: number): void => {
    const current = queryClient.getQueryData<OpportunityStatementsStatusesResult>(queryKey.value);
    if (!current) { return; }
    const patched: OpportunityStatementsStatusesResult = {
      ...current,
      items: current.items.map(s =>
        s.document?.id === documentId
          ? { ...s, document: { ...s.document, state: DocumentState.ExtractionStarted } }
          : s,
      ),
      raw: {
        ...current.raw,
        data: (current.raw.data ?? []).map(s =>
          s.document?.id === documentId
            ? { ...s, document: { ...s.document, state: DocumentState.ExtractionStarted } }
            : s,
        ),
      },
    };
    queryClient.setQueryData(queryKey.value, patched);
  };

  return {
    query,
    statuses,
    rawStatuses,
    alert,
    setData,
    markDocumentAsExtracting,
  };
}
